import React, { useState, useEffect } from 'react';
import {
  BuildingOfficeIcon,
  MagnifyingGlassIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';
import { api } from '../services/authService';
import toast from 'react-hot-toast';
import { validateDocument, getValidationClass, getValidationMessage } from '../utils/validators';

const CompanyDataForm = ({ onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [loadingCNPJ, setLoadingCNPJ] = useState(false);
  const [existingSettings, setExistingSettings] = useState(new Set()); // Track existing keys
  const [cnpjTouched, setCnpjTouched] = useState(false); // Track if CNPJ field was touched
  const [formData, setFormData] = useState({
    company_name: '',
    company_cnpj: '',
    company_address: '',
    company_city: '',
    company_state: '',
    company_cep: '',
    company_phone: '',
    company_email: ''
  });

  // Carregar dados atuais da empresa
  useEffect(() => {
    loadCompanyData();
  }, []);

  const loadCompanyData = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/system-settings');
      const settings = response.data.data || [];

      // Converter array de settings em objeto E rastrear chaves existentes
      const settingsObj = {};
      const existingKeys = new Set();

      settings.forEach(setting => {
        if (setting.key_name && setting.key_name.startsWith('company_')) {
          settingsObj[setting.key_name] = setting.value;
          existingKeys.add(setting.key_name); // Rastrear que esta chave existe
        }
      });

      setExistingSettings(existingKeys);
      setFormData(prev => ({
        ...prev,
        ...settingsObj
      }));
    } catch (error) {
      console.error('Erro ao carregar dados da empresa:', error);
      toast.error('Erro ao carregar dados da empresa');
    } finally {
      setLoading(false);
    }
  };

  const formatCNPJ = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 14) {
      return numbers
        .replace(/^(\d{2})(\d)/, '$1.$2')
        .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
        .replace(/\.(\d{3})(\d)/, '.$1/$2')
        .replace(/(\d{4})(\d)/, '$1-$2');
    }
    return value;
  };

  const formatCEP = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 8) {
      return numbers.replace(/^(\d{5})(\d)/, '$1-$2');
    }
    return value;
  };

  const handleCNPJChange = (e) => {
    const formatted = formatCNPJ(e.target.value);
    setFormData(prev => ({ ...prev, company_cnpj: formatted }));
  };

  const handleCEPChange = (e) => {
    const formatted = formatCEP(e.target.value);
    setFormData(prev => ({ ...prev, company_cep: formatted }));
  };

  const consultCNPJ = async () => {
    const cnpjNumeros = formData.company_cnpj.replace(/\D/g, '');

    if (cnpjNumeros.length !== 14) {
      toast.error('CNPJ inválido! Digite 14 dígitos.');
      return;
    }

    setLoadingCNPJ(true);
    try {
      const response = await api.get(`/api/receitaws/cnpj/${cnpjNumeros}`);
      const result = response.data;

      if (result.success && result.data) {
        const data = result.data;

        // Preencher dados da empresa
        setFormData(prev => ({
          ...prev,
          company_name: data.razao_social || data.nome || prev.company_name,
          company_address: data.logradouro
            ? `${data.logradouro}, ${data.numero || 'S/N'}${data.complemento ? ' - ' + data.complemento : ''} - ${data.bairro}`
            : prev.company_address,
          company_city: data.municipio || prev.company_city,
          company_state: data.uf || prev.company_state,
          company_cep: data.cep ? data.cep.replace(/(\d{5})(\d{3})/, '$1-$2') : prev.company_cep,
          company_phone: data.telefone || prev.company_phone,
          company_email: data.email || prev.company_email
        }));

        toast.success('Dados da empresa carregados com sucesso!');

        // Se houver QSA (sócios), tentar criar/atualizar o sócio principal
        if (data.qsa && data.qsa.length > 0) {
          // Pegar o primeiro sócio administrador ou o primeiro da lista
          const socioAdmin = data.qsa.find(s =>
            s.qual && (
              s.qual.toLowerCase().includes('administrador') ||
              s.qual.toLowerCase().includes('sócio')
            )
          ) || data.qsa[0];

          if (socioAdmin) {
            try {
              // Tentar criar/atualizar sócio principal
              await api.post('/api/company-partners', {
                name: socioAdmin.nome,
                cpf: '', // QSA não retorna CPF
                position: socioAdmin.qual || 'Sócio Administrador',
                is_primary: true,
                is_active: true
              });

              toast.success('Sócio principal atualizado!');
            } catch (partnerError) {
              console.log('Erro ao atualizar sócio:', partnerError);
              // Não mostrar erro para o usuário, é opcional
            }
          }
        }
      } else {
        toast.error(result.message || 'CNPJ não encontrado');
      }
    } catch (error) {
      console.error('Erro ao consultar CNPJ:', error);
      if (error.response?.status === 404) {
        toast.error('CNPJ não encontrado na Receita Federal');
      } else if (error.response?.status === 429) {
        toast.error('Muitas requisições. Aguarde alguns segundos.');
      } else {
        toast.error('Erro ao consultar CNPJ');
      }
    } finally {
      setLoadingCNPJ(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validar CNPJ antes de enviar
    if (formData.cnpj && !validateDocument(formData.cnpj)) {
      toast.error('CNPJ inválido! Por favor, corrija antes de salvar.');
      return;
    }

    setLoading(true);
    try {
      // Atualizar ou criar cada configuração individualmente (UPSERT)
      const updates = Object.keys(formData).map(async (key) => {
        const settingExists = existingSettings.has(key);

        if (settingExists) {
          // UPDATE: configuração já existe - usar PUT /value/:category/:key
          return api.put(`/api/system-settings/value/company/${key}`, {
            value: formData[key]
          });
        } else {
          // INSERT: configuração não existe - usar POST
          return api.post('/api/system-settings', {
            key_name: key,
            value: formData[key],
            category: 'company',
            data_type: 'string',
            editable: true
          });
        }
      });

      await Promise.all(updates);

      toast.success('Dados da empresa atualizados com sucesso!');
      if (onSuccess) onSuccess();

      // Recarregar dados para atualizar existingSettings
      await loadCompanyData();
    } catch (error) {
      console.error('Erro ao salvar dados:', error);
      toast.error('Erro ao salvar dados da empresa');
    } finally {
      setLoading(false);
    }
  };

  if (loading && !formData.company_cnpj) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Carregando...</span>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6 max-w-4xl">
      {/* Header */}
      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
        <div className="flex items-center gap-3">
          <BuildingOfficeIcon className="w-6 h-6 text-blue-600" />
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Dados da Empresa
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Configure as informações da sua empresa. Use o botão "Consultar CNPJ" para preencher automaticamente.
            </p>
          </div>
        </div>
      </div>

      {/* CNPJ com botão de consulta */}
      <div className="space-y-4">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          CNPJ *
        </label>
        <div className="flex gap-2">
          <div className="flex-1">
            <input
              type="text"
              value={formData.company_cnpj}
              onChange={handleCNPJChange}
              onBlur={() => setCnpjTouched(true)}
              placeholder="00.000.000/0001-00"
              maxLength={18}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white ${getValidationClass(formData.company_cnpj, cnpjTouched) || 'border-gray-300 dark:border-gray-600'}`}
              required
            />
            {cnpjTouched && formData.company_cnpj && (
              <p className={`text-xs mt-1 ${getValidationMessage(formData.company_cnpj, cnpjTouched).isValid ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                {getValidationMessage(formData.company_cnpj, cnpjTouched).message}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={consultCNPJ}
            disabled={loadingCNPJ || formData.company_cnpj.replace(/\D/g, '').length !== 14}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg flex items-center gap-2 transition-colors"
          >
            {loadingCNPJ ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Consultando...
              </>
            ) : (
              <>
                <MagnifyingGlassIcon className="w-5 h-5" />
                Consultar CNPJ
              </>
            )}
          </button>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Digite o CNPJ e clique em "Consultar CNPJ" para preencher automaticamente os dados da Receita Federal
        </p>
      </div>

      {/* Grid de campos */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Razão Social */}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Razão Social *
          </label>
          <input
            type="text"
            value={formData.company_name}
            onChange={(e) => setFormData(prev => ({ ...prev, company_name: e.target.value }))}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
            required
          />
        </div>

        {/* Endereço */}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Endereço *
          </label>
          <input
            type="text"
            value={formData.company_address}
            onChange={(e) => setFormData(prev => ({ ...prev, company_address: e.target.value }))}
            placeholder="Rua Exemplo, 123 - Centro"
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
            required
          />
        </div>

        {/* Cidade */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Cidade *
          </label>
          <input
            type="text"
            value={formData.company_city}
            onChange={(e) => setFormData(prev => ({ ...prev, company_city: e.target.value }))}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
            required
          />
        </div>

        {/* Estado */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Estado *
          </label>
          <input
            type="text"
            value={formData.company_state}
            onChange={(e) => setFormData(prev => ({ ...prev, company_state: e.target.value.toUpperCase() }))}
            placeholder="MS"
            maxLength={2}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
            required
          />
        </div>

        {/* CEP */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            CEP *
          </label>
          <input
            type="text"
            value={formData.company_cep}
            onChange={handleCEPChange}
            placeholder="00000-000"
            maxLength={9}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
            required
          />
        </div>

        {/* Telefone */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Telefone
          </label>
          <input
            type="text"
            value={formData.company_phone}
            onChange={(e) => setFormData(prev => ({ ...prev, company_phone: e.target.value }))}
            placeholder="(67) 99999-9999"
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
          />
        </div>

        {/* Email */}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            E-mail
          </label>
          <input
            type="email"
            value={formData.company_email}
            onChange={(e) => setFormData(prev => ({ ...prev, company_email: e.target.value }))}
            placeholder="contato@viploc.com.br"
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
          />
        </div>
      </div>

      {/* Botões */}
      <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
        <button
          type="button"
          onClick={loadCompanyData}
          className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={loading}
          className="px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg flex items-center gap-2 transition-colors"
        >
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              Salvando...
            </>
          ) : (
            <>
              <CheckCircleIcon className="w-5 h-5" />
              Salvar Alterações
            </>
          )}
        </button>
      </div>
    </form>
  );
};

export default CompanyDataForm;
